import React from 'react'
import {FormattedNumber} from 'react-intl'

import {Button, ButtonSize} from '@vio/atlas/Button'
import {Icon} from '@vio/atlas/Icon'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {PriceTrendMessageKey} from '../../utils/messages'

interface PriceTrendButtonProps {
  messageKey: PriceTrendMessageKey
  priceValue: number
  currency: string | null
  isFullWidth?: boolean
  size?: ButtonSize
  hasArrow?: boolean
  /** When set, the button is a plain action (no OfferInteraction navigation). */
  onClick?: () => void
}

const NUMBER_OF_DECIMALS_FOR_PRICE = 0

const getButtonContent = (
  messageKey: PriceTrendMessageKey,
  formattedPrice: JSX.Element
) => {
  switch (messageKey) {
    case 'freezeDealsFromValue':
      return (
        <FormattedMessageWrapper
          id="freezeDealsFromValue"
          defaultMessage="Freeze deals from {value}"
          values={{value: formattedPrice}}
        />
      )
    case 'viewDealFromValue':
      return (
        <FormattedMessageWrapper
          id="viewDealFromValue"
          defaultMessage="View deal from {value}"
          values={{value: formattedPrice}}
        />
      )
    case 'priceInsightsAdviceViewFreezableDeals':
      return (
        <FormattedMessageWrapper
          id="priceInsightsAdviceViewFreezableDeals"
          defaultMessage="View freezable deals"
        />
      )
    default:
      return (
        <FormattedMessageWrapper
          id="viewRoomsFromValue"
          defaultMessage="View rooms from {value}"
          values={{value: formattedPrice}}
        />
      )
  }
}

/**
 * Simple button component for PriceTrend.
 * Renders a button with message like "View rooms from €XXX" or "View deal from €XXX".
 * For mobile redirect layout (logo + price + button), use PriceTrendRedirectLayout instead.
 */
export const PriceTrendButton = ({
  messageKey,
  priceValue,
  currency,
  size,
  isFullWidth = true,
  hasArrow = false,
  onClick
}: PriceTrendButtonProps) => {
  const formattedPrice = (
    <FormattedNumber
      currency={currency}
      style="currency"
      minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
      maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
      value={priceValue}
    />
  )

  return (
    <Button
      fullWidth={isFullWidth}
      size={size}
      dataId="price-trend-button"
      data-message-key={messageKey}
      onClick={onClick}
      iconEnd={hasArrow ? <Icon name="ChevronRight" /> : undefined}
    >
      {getButtonContent(messageKey, formattedPrice)}
    </Button>
  )
}
